import { useSyncExternalStore } from "react";

/**
 * Player language. Vietnamese is the source language and the default for
 * every new player; English is opt-in from the first screen or Settings and
 * remembered on this device (read before the first paint, so even the
 * loading screen speaks the right language).
 *
 * Copy tables (`vi`, `companionVi`, ...) keep their Vietnamese source and
 * register an English twin with `translate()`. Switching language rewrites
 * each registered table in place, so code that reads `vi.settings.title`
 * at render time follows the switch without importing anything new. Game
 * content (houses, items, chapters, tutorials) is looked up by id at display
 * time instead, see `content.ts`.
 */
export type Language = "vi" | "en";
export const LANGUAGES: readonly Language[] = ["vi", "en"];

/** Each language names itself, so a lost player can always find their own. */
export const LANGUAGE_NAMES: Record<Language, string> = { vi: "Tiếng Việt", en: "English" };

const STORAGE_KEY = "nano.lang";

function stored(): Language {
  try {
    return localStorage.getItem(STORAGE_KEY) === "en" ? "en" : "vi";
  } catch {
    return "vi";
  }
}

let current: Language = stored();
const listeners = new Set<() => void>();

export function language(): Language {
  return current;
}

/** BCP 47 tag for numbers and times. */
export function locale(): string {
  return current === "en" ? "en-US" : "vi-VN";
}

export function formatNumber(n: number): string {
  return n.toLocaleString(locale());
}

/**
 * Same shape as a Vietnamese table with every text widened to `string`;
 * lists may have a different length (the companion's lines).
 */
export type Translation<T> = T extends string
  ? string
  : T extends (...args: infer A) => infer R
    ? (...args: A) => R extends string ? string : R
    : T extends readonly (infer U)[]
      ? readonly Translation<U>[]
      : T extends object
        ? { readonly [K in keyof T]: Translation<T[K]> }
        : T;

type Node = Record<string, unknown>;

const isNode = (value: unknown): value is Node => typeof value === "object" && value !== null && !Array.isArray(value);

/** Writes `source` onto `target` without replacing any object or list a caller may hold. */
function overlay(target: Node, source: Node): void {
  for (const [key, value] of Object.entries(source)) {
    const slot = target[key];
    if (Array.isArray(value) && Array.isArray(slot)) slot.splice(0, slot.length, ...(value as unknown[]));
    else if (isNode(value) && isNode(slot)) overlay(slot, value);
    else target[key] = value;
  }
}

/** Copies objects and lists; strings and functions are shared. */
function snapshot<T>(value: T): T {
  if (Array.isArray(value)) return value.map(snapshot) as T;
  if (isNode(value)) return Object.fromEntries(Object.entries(value).map(([key, inner]) => [key, snapshot(inner)])) as T;
  return value;
}

const tables: { live: Node; vi: Node; en: Node }[] = [];

/** Registers a copy table and its English twin; returns the table, now following the player's language. */
export function translate<T extends object>(table: T, en: Translation<T>): Translation<T> {
  const entry = { live: table as Node, vi: snapshot(table) as Node, en: en as Node };
  tables.push(entry);
  if (current === "en") overlay(entry.live, entry.en);
  return table as Translation<T>;
}

export function setLanguage(next: Language): void {
  if (next === current) return;
  current = next;
  try {
    localStorage.setItem(STORAGE_KEY, next);
  } catch {
    // Private mode: the choice lasts for this visit.
  }
  for (const entry of tables) overlay(entry.live, entry[next]);
  if (typeof document !== "undefined") document.documentElement.lang = next;
  for (const listener of listeners) listener();
}

export function onLanguageChange(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** Re-renders the caller (the app root re-renders everything) when the language changes. */
export function useLanguage(): Language {
  return useSyncExternalStore(onLanguageChange, language);
}

if (typeof document !== "undefined") document.documentElement.lang = current;
